import {useEffect, useMemo, useRef} from 'react';
import {useSearchParams} from 'react-router-dom';
import getPlatformFromIdentityProvider from '@/utils/getIPlatformFromIdentityProvider';
import {Identity} from '@/types/identity';
import useFilteredTiktokSocialNetworks from '@/hooks/use-filtered-tiktok-social-networks';
import useSortedSocialNetworks from '@/hooks/use-sorted-social-networks';
import {useCampaignNetwork} from '@/contexts/CampaignNetworkContext';

interface SocialNetworksSelector {
  socialNetworks: Identity[];
  activeIdentity: Identity | null;
  onSocialNetworkClick: (socialNetwork: Identity) => void;
}

function useSocialNetworksSelector(
  identities: Identity[]
): SocialNetworksSelector {
  const campaignNetwork = useCampaignNetwork();
  const [searchParams, setSearchParams] = useSearchParams();

  const socialNetworks = useSortedSocialNetworks(
    identities.filter((identity) => identity.socialNetwork?.supportsSocialData)
  );
  const filteredSocialNetworks = useFilteredTiktokSocialNetworks(
    socialNetworks
  ) as Identity[];

  const campaignNetworkMatch = campaignNetwork
    ? filteredSocialNetworks.find(
        (identity) =>
          getPlatformFromIdentityProvider(identity.provider) ===
          getPlatformFromIdentityProvider(campaignNetwork)
      )?.provider
    : undefined;

  const hasAppliedCampaignNetwork = useRef(false);
  useEffect(() => {
    if (
      campaignNetworkMatch &&
      !hasAppliedCampaignNetwork.current &&
      !searchParams.get('activeSocialNetwork')
    ) {
      hasAppliedCampaignNetwork.current = true;
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('activeSocialNetwork', campaignNetworkMatch);
      setSearchParams(newSearchParams, {replace: true});
    }
  }, [campaignNetworkMatch, searchParams, setSearchParams]);

  const activeSocialNetworkId =
    searchParams.get('activeSocialNetwork') ||
    filteredSocialNetworks[0]?.provider ||
    '';
  const activeIdentity = useMemo(() => {
    return (
      filteredSocialNetworks.find(
        (socialNetwork) => socialNetwork.provider === activeSocialNetworkId
      ) ||
      filteredSocialNetworks[0] ||
      null
    );
  }, [filteredSocialNetworks, activeSocialNetworkId]);

  const onSocialNetworkClick = (socialNetwork: Identity) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('activeSocialNetwork', socialNetwork.provider);
    setSearchParams(newSearchParams);
  };

  return {
    socialNetworks: filteredSocialNetworks,
    activeIdentity,
    onSocialNetworkClick,
  };
}

export default useSocialNetworksSelector;
